/**
 * The pilot's price log (M8 plan TR-1a; TR-1b records): the best known buy and sell price per ware in each system
 * the pilot entered or covers (`career.priceLog`, MC-0a's `PriceLogEntry`). Pure, Career-in, Career-out.
 *
 * - Source: a `SystemEconomyView`'s per-station market rows (`MarketRow.buyUnit` is what the pilot pays,
 *   `sellUnit` what the station pays), never the galaxy view. Only dockable stations count.
 * - One entry per system, in strategic ware order (`WARE_COUNT` long): `b` the lowest buy unit, `s` the highest sell
 *   unit, both in whole credits, and `bAt`/`sAt` the index in `view.stations` of the station that quoted it.
 *   −1 = no data. Per-station prices are live-only (decision 2: they would cost save bytes). The rows stay plain
 *   integer arrays (MC-0a's schema): the real log fits the 96 KB record budget without a compact encoding.
 * - A record replaces that system's entry (fresh prices, fresh `t`); systems are never dropped. The caller (TR-1b)
 *   decides which systems are recorded: only the ones the pilot entered or covers.
 * - Ages are in strategic faction days (`strategic.json clock.dayS`) measured on the career clock.
 */
import strategicJson from '../data/strategic/strategic.json'
import type { Career, PriceLogEntry } from '../career/types'
import type { SystemEconomyView } from '../strategic/types'
import { WARE_COUNT, wareIndex, type WareId as StrategicWareId } from '../strategic/wares'

const DAY_S: number = strategicJson.clock.dayS
const NO_DATA = -1

export type PriceSide = 'buy' | 'sell'

/** What the log reads from a system view: its id and its stations' market rows. */
export type PriceSource = Pick<SystemEconomyView, 'systemId' | 'stations'>

export interface KnownPrice {
  /** Whole credits per t. */
  unit: number
  /** The station index (in the system view's `stations` order) that quoted it. */
  station: number
  /** Faction days since it was seen (0 when live). */
  ageDays: number
  /** Read from the live view (a covered system), not the log. */
  live: boolean
}

function noDataRow(): number[] {
  return new Array<number>(WARE_COUNT).fill(NO_DATA)
}

function wholeCredits(unit: number): number {
  return Math.max(0, Math.round(unit))
}

/** The system's best prices in `view` at game time `now` (whole credits; −1 = no data). */
export function priceLogEntry(view: PriceSource, now: number): PriceLogEntry {
  const b = noDataRow()
  const s = noDataRow()
  const bAt = noDataRow()
  const sAt = noDataRow()
  const bestBuy = new Array<number>(WARE_COUNT).fill(Infinity)
  const bestSell = new Array<number>(WARE_COUNT).fill(-Infinity)
  view.stations.forEach((st, i) => {
    if (!st.dockable) return
    for (const row of st.wares) {
      const w = wareIndex(row.ware)
      if (row.buyUnit !== null && Number.isFinite(row.buyUnit) && row.buyUnit < bestBuy[w]) {
        bestBuy[w] = row.buyUnit
        bAt[w] = i
      }
      if (row.sellUnit !== null && Number.isFinite(row.sellUnit) && row.sellUnit > bestSell[w]) {
        bestSell[w] = row.sellUnit
        sAt[w] = i
      }
    }
  })
  for (let w = 0; w < WARE_COUNT; w++) {
    if (bAt[w] !== NO_DATA) b[w] = wholeCredits(bestBuy[w])
    if (sAt[w] !== NO_DATA) s[w] = wholeCredits(bestSell[w])
  }
  return { t: now, b, s, bAt, sAt }
}

/** Records the system's best prices from `view` at game time `now`, replacing that system's entry. */
export function recordPrices(career: Career, view: PriceSource, now: number): Career {
  return { ...career, priceLog: { ...career.priceLog, [view.systemId]: priceLogEntry(view, now) } }
}

/** The live best price of `ware` on `side` in `view`, or station index `station`'s own price; null = none. */
function livePrice(view: PriceSource, ware: StrategicWareId, side: PriceSide, station: number | undefined): KnownPrice | null {
  if (station === undefined) {
    const entry = priceLogEntry(view, 0)
    const w = wareIndex(ware)
    const at = side === 'buy' ? entry.bAt[w] : entry.sAt[w]
    if (at === NO_DATA) return null
    return { unit: side === 'buy' ? entry.b[w] : entry.s[w], station: at, ageDays: 0, live: true }
  }
  const st = view.stations[station]
  if (st === undefined || !st.dockable) return null
  const row = st.wares.find((r) => r.ware === ware)
  const unit = row === undefined ? null : side === 'buy' ? row.buyUnit : row.sellUnit
  if (unit === null || !Number.isFinite(unit)) return null
  return { unit: wholeCredits(unit), station, ageDays: 0, live: true }
}

/**
 * What the pilot knows of `ware`'s price on `side` ('buy': the lowest the pilot pays; 'sell': the highest a station
 * pays) in `systemId`. In a covered system (`view` is that system's live view) it reads the live view, for any
 * station when `station` (an index in `view.stations`) is given. Elsewhere it returns the logged system best with its
 * station, whatever `station` asks for: a non-best station has no logged price of its own (review 1 m6). Null = no
 * data.
 */
export function knownPrice(
  view: PriceSource | null,
  career: Career,
  systemId: string,
  ware: StrategicWareId,
  side: PriceSide,
  station?: number,
): KnownPrice | null {
  if (view !== null && view.systemId === systemId) return livePrice(view, ware, side, station)
  const entry = career.priceLog?.[systemId]
  if (entry === undefined) return null
  const w = wareIndex(ware)
  const unit = side === 'buy' ? entry.b[w] : entry.s[w]
  const at = side === 'buy' ? entry.bAt[w] : entry.sAt[w]
  if (unit === undefined || at === undefined || unit === NO_DATA || at === NO_DATA) return null
  return { unit, station: at, ageDays: Math.max(0, career.gameTime - entry.t) / DAY_S, live: false }
}
